/* Vimaana Garten Team — Design-Port: Icons, Format-Helfer, Mapping-Hook.
   Bildet die Live-API (team.ts) auf die Shapes des Design-Bundles ab. */
import { useMemo } from "react";
import {
  useTeamMe, useStunden, useSaldo, useEinsaetze, useOffeneStellen, useRechnungen,
  useBestaetigen, useAnpassen, useBewerben, useProjekteOptionen, useProjekteFuerFotos, useNeueStunden,
  useRechnungUpload, useRechnungErsetzen, rechnungDateiUrl, rechnungAnalysieren, useFotoUpload, useAuslagen,
  useProjektpreise, useBenachrichtigungen, useBenachrichtigungenGelesen,
  useProjekteAktiv, useLieferscheinUpload,
  useEinsatzZusagen, useEinsatzAbsagen,
  type ProjektOption, type RechnungUploadArgs, type FotoUploadArgs, type FotoUploadResult,
  type LieferscheinArgs, type LieferscheinResult,
  type Projektpreis, type Benachrichtigung,
} from "./team";

export type { ProjektOption, RechnungUploadArgs, FotoUploadArgs, FotoUploadResult, Projektpreis, Benachrichtigung };
export type { LieferscheinArgs, LieferscheinResult };
// Reine Lesehilfe fuers Rechnungsformular — kein Hook, deshalb direkt durchgereicht.
export { rechnungAnalysieren };

// Der Versionshinweis wird nicht mehr von Hand gepflegt: `TEAM_VERSION` stand
// zuletzt monatelang auf 0.6.2, während die App weiterlief — eine vergessene
// Nummer weist eine veraltete App als aktuell aus. Jetzt kommt der Stand aus
// dem Build (`@vimaana/ui`: `laufenderBuild`, `MVersionsZeile`).

// ─────────────────────────── Icons ────────────────────────────────────────
const PATHS: Record<string, JSX.Element> = {
  home: <><path d="M4 11l8-7 8 7" /><path d="M6 9.5V20h12V9.5" /><path d="M10 20v-5h4v5" /></>,
  calendar: <><rect x="4" y="5.5" width="16" height="15" rx="2.5" /><path d="M4 10h16" /><path d="M8 3.5v4M16 3.5v4" /></>,
  briefcase: <><rect x="3.5" y="7.5" width="17" height="12" rx="2.5" /><path d="M9 7.5V6a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v1.5" /><path d="M3.5 12h17" /></>,
  clock: <><circle cx="12" cy="12" r="8.5" /><path d="M12 7.5V12l3 2" /></>,
  euro: <><path d="M16.5 7.2A6 6 0 1 0 16.5 16.8" /><path d="M5 11h7M5 14h6" /></>,
  wallet: <><rect x="3.5" y="6" width="17" height="13" rx="2.5" /><path d="M16 12.5h2.5" /><path d="M3.5 9.5h13a1.5 1.5 0 0 1 0 3" /></>,
  person: <><circle cx="12" cy="8.5" r="3.7" /><path d="M5 20a7 7 0 0 1 14 0" /></>,
  back: <path d="M15 5l-7 7 7 7" />,
  chev: <path d="M9 5l7 7-7 7" />,
  bell: <><path d="M6 10a6 6 0 0 1 12 0c0 5 2 6 2 6H4s2-1 2-6z" /><path d="M10.5 20a1.7 1.7 0 0 0 3 0" /></>,
  check: <path d="M5 12.5 10 17 19 7" />,
  pin: <><path d="M12 21s7-6.3 7-11a7 7 0 0 0-14 0c0 4.7 7 11 7 11z" /><circle cx="12" cy="10" r="2.5" /></>,
  map: <><path d="M9 4.5 3.5 6.5v13L9 17.5l6 2 5.5-2v-13L15 6.5l-6-2z" /><path d="M9 4.5v13M15 6.5v13" /></>,
  phone: <path d="M6.5 4h3l1.5 4-2 1.5a11 11 0 0 0 5 5l1.5-2 4 1.5v3a2 2 0 0 1-2.2 2A16 16 0 0 1 4.5 6.2 2 2 0 0 1 6.5 4z" />,
  chat: <path d="M21 11.5a8.4 8.4 0 0 1-12.3 7.5L3 20.5l1.6-5A8.4 8.4 0 1 1 21 11.5z" />,
  edit: <><path d="M14 5l5 5" /><path d="M4 20l1-4L16 5l3 3L8 19z" /></>,
  team: <><circle cx="9" cy="9" r="3" /><path d="M3.5 19a5.5 5.5 0 0 1 11 0" /><path d="M16 7.5a3 3 0 0 1 0 5.8M16.5 19a5.5 5.5 0 0 0-2-4.2" /></>,
  list: <><path d="M8 6h12M8 12h12M8 18h12" /><circle cx="4" cy="6" r="1" fill="currentColor" stroke="none" /><circle cx="4" cy="12" r="1" fill="currentColor" stroke="none" /><circle cx="4" cy="18" r="1" fill="currentColor" stroke="none" /></>,
  doc: <><path d="M6 3.5h8l4 4V20a.5.5 0 0 1-.5.5h-11A.5.5 0 0 1 6 20z" /><path d="M14 3.5V8h4" /><path d="M9 13h6M9 16h4" /></>,
  receipt: <><path d="M6 3.5h12v17l-2-1.3-2 1.3-2-1.3-2 1.3-2-1.3-2 1.3z" /><path d="M9 8h6M9 11.5h6" /></>,
  lock: <><rect x="5" y="10.5" width="14" height="9.5" rx="2.2" /><path d="M8 10.5V8a4 4 0 0 1 8 0v2.5" /></>,
  mail: <><rect x="3.5" y="5.5" width="17" height="13" rx="2.5" /><path d="M4 7l8 5.5L20 7" /></>,
  plus: <><path d="M12 5v14" /><path d="M5 12h14" /></>,
  minus: <path d="M5 12h14" />,
  sparkle: <path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8z" />,
  info: <><circle cx="12" cy="12" r="9" /><path d="M12 11v5" /><circle cx="12" cy="7.8" r="0.9" fill="currentColor" stroke="none" /></>,
  arrowup: <path d="M12 19V5M6 11l6-6 6 6" />,
  camera: <><path d="M4 8.5h3.2l1.6-2.5h6.4l1.6 2.5H20a.5.5 0 0 1 .5.5v9.5a.5.5 0 0 1-.5.5H4a.5.5 0 0 1-.5-.5V9a.5.5 0 0 1 .5-.5z" /><circle cx="12" cy="13" r="3.2" /></>,
  flag: <><path d="M6 21V4" /><path d="M6 4h11l-2 3.5L17 11H6" /></>,
};

export function VGTIcon({ name, style }: { name: string; style?: React.CSSProperties }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.7}
         strokeLinecap="round" strokeLinejoin="round" style={style}>
      {PATHS[name] || null}
    </svg>
  );
}

// ─────────────────────────── Format-Helfer ────────────────────────────────
export const eur = (n: number | null | undefined) =>
  n == null ? "—" : n.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
export const std = (n: number) => (Number.isInteger(n) ? n.toString() : n.toFixed(2).replace(/0$/, "").replace(".", ",")) + " h";
export const mapsUrl = (a: string) => "https://www.google.com/maps/search/?api=1&query=" + encodeURIComponent(a);
export const initials = (name: string) => (name || "?").trim().split(/\s+/).slice(0, 2).map(p => p[0] || "").join("").toUpperCase();
const dObj = (iso: string) => new Date(iso.length <= 10 ? iso + "T12:00:00" : iso);
export const wtag = (iso: string) => dObj(iso).toLocaleDateString("de-DE", { weekday: "short" }).replace(".", "");
export const tag = (iso: string) => dObj(iso).getDate();
export const monatKurz = (iso: string) => dObj(iso).toLocaleDateString("de-DE", { month: "short" }).replace(".", "");
export const langDatum = (iso: string) => dObj(iso).toLocaleDateString("de-DE", { weekday: "long", day: "2-digit", month: "long" });
export const hm = (iso: string) => { if (!iso) return ""; const d = new Date(iso); const p = (n: number) => String(n).padStart(2, "0"); return `${p(d.getHours())}:${p(d.getMinutes())}`; };
/** „vor 5 Min." / „vor 3 Std." / „gestern" — wie neu ist diese Nachricht? */
export function relZeit(iso: string): string {
  if (!iso) return "";
  const min = Math.round((Date.now() - new Date(iso).getTime()) / 60_000);
  if (!Number.isFinite(min) || min < 0) return "";
  if (min < 2) return "gerade eben";
  if (min < 60) return `vor ${min} Min.`;
  const std = Math.round(min / 60);
  if (std < 24) return `vor ${std} Std.`;
  const tage = Math.round(std / 24);
  return tage === 1 ? "gestern" : `vor ${tage} Tagen`;
}

export function relTag(iso: string): string | null {
  const d = dObj(iso); const t = new Date(); t.setHours(12, 0, 0, 0);
  const diff = Math.round((d.getTime() - t.getTime()) / 86400000);
  if (diff === 0) return "Heute";
  if (diff === 1) return "Morgen";
  if (diff === -1) return "Gestern";
  if (diff > 1 && diff < 7) return "in " + diff + " Tagen";
  return null;
}

// ─────────────────────────── Typen (Design-Shapes) ────────────────────────
export type HourStatus = "zu_bestaetigen" | "korrektur" | "bestaetigt" | "abgelehnt";
export interface Hour {
  id: string; projekt: string; datum: string; von: string; bis: string; startAt: string;
  erfasstStd: number; geplantStd: number; pause: number; betrag: number; note: string;
  zusatzkosten: number;
  status: HourStatus;
  /** Bei status='abgelehnt': Begründung des Inhabers. */
  ablehnungText: string;
  projektId: string;
  /** Vergütung: 'stundenlohn' oder 'pauschale' (an einen Projektpreis gehängt). */
  verguetungsart: string;
  pauschaleId: string | null;
  pauschaleBezeichnung: string;
  pauschaleBetrag: number;
  pauschaleStatus: string;
}
export interface Einsatz {
  id: string; terminId: string | null; projektId: string; projekt: string; taetigkeit: string; adresse: string;
  /** Kunde des Projekts — leer, wenn keiner hinterlegt ist. */
  kunde: string;
  datum: string; von: string; bis: string; status: string; hinweis: string;
  einsatzStatus: import("./types").EinsatzStatus;
  pauseStd: number; stundenlohn: number; geplantNetto: number;
  /** Vergütung DIESES Termins: 'stundenlohn' oder 'pauschale'. */
  verguetungsart: string;
  pauschaleId: string | null;
  pauschaleBezeichnung: string;
  pauschaleBetrag: number;
  pauschaleStatus: string;
  /** Anzahl eigener Termine, die an derselben Pauschale hängen. */
  pauschaleSlots: number;
  /** Einsatz ist heute. */
  heute: boolean;
  /** Wenn die Bauleitung die Bewerbung abgelehnt hat: ihr Satz dazu. */
  absageGrund: string;
  /** Kürzlich verlegt? Dann steht hier „Verlegt: alt → neu". */
  verschoben: string;
}
export interface Job {
  id: string; projekt: string; ort: string; adresse: string; kunde: string; datum: string; von: string; bis: string;
  satz: number; plaetze: number; gemeldet: number; frei: number; beschreibung: string;
  /** Geplante Dauer der Stelle in Stunden (brutto — eine Pause ist noch nicht geplant). */
  geplantStd: number;
}

// ─────────────────────────── Verdienst je Termin ──────────────────────────
export type Verdienst = { art: "stundenlohn" | "pauschale"; betragText: string; detail: string };

/** Was bringt mir dieser Termin? Entweder Stundenlohn × geplante Netto-Stunden
 *  oder — wenn der Termin an einem Projektpreis hängt — dessen Pauschale.
 *  Die Pauschale wird NICHT auf die Termine aufgeteilt: sie ist fürs Projekt
 *  vereinbart, nicht je Termin. Stattdessen steht dabei, für wie viele Termine
 *  sie gilt, damit niemand sie mehrfach erwartet. */
export function jobVerdienst(e: Einsatz): Verdienst | null {
  if (e.verguetungsart === "pauschale" && e.pauschaleId) {
    const bez = e.pauschaleBezeichnung ? e.pauschaleBezeichnung : "Pauschale";
    const teile = [bez];
    if (e.pauschaleSlots > 1) teile.push(`gilt für ${e.pauschaleSlots} Termine`);
    if (e.pauschaleStatus && e.pauschaleStatus !== "bestaetigt") teile.push("noch nicht fest");
    return { art: "pauschale", betragText: eur(e.pauschaleBetrag), detail: teile.join(" · ") };
  }
  if (e.stundenlohn > 0 && e.geplantNetto > 0) {
    return {
      art: "stundenlohn",
      betragText: eur(e.stundenlohn * e.geplantNetto),
      detail: `${std(e.geplantNetto)} × ${e.stundenlohn.toLocaleString("de-DE", { minimumFractionDigits: 2 })} €/Std.`,
    };
  }
  return null;
}

/** Dasselbe für eine offene Stelle — dort gibt es noch keinen eigenen Slot,
 *  also rechnet die App mit dem eigenen Stundensatz und der geplanten Dauer.
 *  Bewusst als „ca." ausgewiesen: Pause und Ist-Zeit stehen noch nicht fest. */
export function stelleVerdienst(j: Job): Verdienst | null {
  if (!(j.satz > 0 && j.geplantStd > 0)) return null;
  return {
    art: "stundenlohn",
    betragText: "ca. " + eur(j.satz * j.geplantStd),
    detail: `${std(j.geplantStd)} × ${j.satz.toLocaleString("de-DE", { minimumFractionDigits: 2 })} €/Std.`,
  };
}
export interface Auszahlung { id: string; betrag: number; datum: string; vermerk: string; belegId: string; }
export interface RechnungV {
  id: string;
  lieferant: string;
  datum: string;
  betrag: number;
  status: string;
  is_bezahlt: boolean;
  bezahlt_am: string | null;
  hat_datei: boolean;
  dateiname: string;
  rechnungsart: "stunden" | "pauschale";
  projekt_id: string;
  projekt_title: string;
  auszahlung_id: string;
  auszahlung_am: string;
  auszahlung_betrag: number;
  ersetzbar: boolean;
}

const mapStatus = (h: string): HourStatus =>
  h === "bestaetigt" ? "bestaetigt"
  : h === "pending_inhaber" ? "korrektur"
  : h === "abgelehnt" ? "abgelehnt"
  : "zu_bestaetigen";

// ─────────────────────────── Modell-Hook ──────────────────────────────────
export function useTeamModel() {
  const me = useTeamMe();
  const stundenQ = useStunden();
  const saldoQ = useSaldo();
  const einsaetzeQ = useEinsaetze();
  const jobsQ = useOffeneStellen();
  const rechnungenQ = useRechnungen();

  const projekteQ = useProjekteOptionen();       // aktive Projekte — Stunden erfassen
  const fotoProjekteQ = useProjekteFuerFotos();  // alle je zugeteilten — Foto-Upload
  const aktiveProjekteQ = useProjekteAktiv();    // alle laufenden — Lieferschein
  const bestaetigen = useBestaetigen();
  const anpassen = useAnpassen();
  const auslagen = useAuslagen();
  const bewerben = useBewerben();
  const einsatzZusagen = useEinsatzZusagen();
  const einsatzAbsagen = useEinsatzAbsagen();
  const neueStunden = useNeueStunden();
  const rechnungUpload = useRechnungUpload();
  const rechnungErsetzen = useRechnungErsetzen();
  const fotoUpload = useFotoUpload();
  const lieferschein = useLieferscheinUpload();
  const projektpreiseQ = useProjektpreise();
  const nachrichtenQ = useBenachrichtigungen();
  const nachrichtenGelesen = useBenachrichtigungenGelesen();

  const user = useMemo(() => {
    const m = me.data;
    if (!m) return null;
    const vorname = (m.name || "").trim().split(/\s+/)[0] || m.name;
    return { id: m.id, name: m.name, vorname, initialen: initials(m.name), satz: m.hourly_rate || 0, role: m.role || "Helfer", telefon: m.phone || "", email: m.email || "" };
  }, [me.data]);

  const hours: Hour[] = useMemo(() => (stundenQ.data || []).map(s => ({
    id: s.slot_id, projekt: s.projekt_title || "Ohne Projekt", datum: s.start_at ? s.start_at.slice(0, 10) : "",
    von: hm(s.start_at), bis: hm(s.end_at), startAt: s.start_at,
    erfasstStd: s.stunden_netto, geplantStd: s.stunden_netto, pause: s.pause_stunden, betrag: s.betrag,
    note: s.note || "", zusatzkosten: s.zusatzkosten || 0, status: mapStatus(s.helfer_status),
    ablehnungText: s.ablehnung_text || "",
    projektId: s.projekt_id || "",
    verguetungsart: s.verguetungsart || "stundenlohn",
    pauschaleId: s.pauschale_id ?? null,
    pauschaleBezeichnung: s.pauschale_bezeichnung || "",
    pauschaleBetrag: s.pauschale_betrag || 0,
    pauschaleStatus: s.pauschale_status || "",
  })), [stundenQ.data]);

  const alleEinsaetze: Einsatz[] = useMemo(() => (einsaetzeQ.data || []).map(e => {
    const grossH = (e.start_at && e.end_at)
      ? (new Date(e.end_at).getTime() - new Date(e.start_at).getTime()) / 3600_000 : 0;
    const geplantNetto = Math.max(0, grossH - (e.pause_stunden || 0));
    return {
      id: e.slot_id, terminId: e.termin_id, projektId: e.projekt_id || "", projekt: e.projekt_title || "Einsatz",
      // Was an dem Tag passiert: der Titel des Termins (Kontor, 14.10.2026), sonst wie bisher.
      taetigkeit: e.aufgabe || e.note || e.projekt_title || "Einsatz", adresse: e.adresse || "",
      kunde: e.kunde_name || "",
      datum: e.start_at ? e.start_at.slice(0, 10) : "", von: hm(e.start_at), bis: hm(e.end_at),
      status: e.confirm_status || "geplant", hinweis: e.note || "",
      einsatzStatus: e.einladungs_status || "wartet_auf_helfer",
      pauseStd: e.pause_stunden || 0, stundenlohn: e.stundenlohn || 0, geplantNetto,
      verguetungsart: e.verguetungsart || "stundenlohn",
      pauschaleId: e.pauschale_id ?? null,
      pauschaleBezeichnung: e.pauschale_bezeichnung || "",
      pauschaleBetrag: e.pauschale_betrag || 0,
      pauschaleStatus: e.pauschale_status || "",
      pauschaleSlots: e.pauschale_slots || 0,
      heute: !!e.heute,
      absageGrund: e.absage_grund || "",
      verschoben: e.verschoben_hinweis || "",
    };
  }), [einsaetzeQ.data]);

  // Eine abgelehnte Bewerbung steht weiter in der Liste — der Helfer soll
  // erfahren, dass es nichts wurde, statt dass sie wortlos verschwindet.
  // Sie ist aber kein Einsatz: sie zählt nicht als „geplant", taucht nicht in
  // auf der Startseite auf und ist nie der „nächste Einsatz". Deshalb zwei Listen.
  const einsaetze = useMemo(
    () => alleEinsaetze.filter(e => e.einsatzStatus !== "abgelehnt_vom_inhaber"),
    [alleEinsaetze],
  );
  const abgelehnteBewerbungen = useMemo(
    () => alleEinsaetze.filter(e => e.einsatzStatus === "abgelehnt_vom_inhaber"),
    [alleEinsaetze],
  );

  const jobs: Job[] = useMemo(() => (jobsQ.data || []).map(j => ({
    id: j.termin_id, projekt: j.projekt_title || "Offene Stelle", ort: "", adresse: j.adresse || "",
    kunde: j.kunde_name || "",
    datum: j.start_at ? j.start_at.slice(0, 10) : "", von: hm(j.start_at), bis: hm(j.end_at),
    satz: user?.satz || 0, plaetze: j.benoetigte_helfer, gemeldet: j.zugewiesene_helfer, frei: j.freie_plaetze,
    beschreibung: j.aufgabe || j.notiz || "",
    geplantStd: (j.start_at && j.end_at)
      ? Math.max(0, (new Date(j.end_at).getTime() - new Date(j.start_at).getTime()) / 3600_000) : 0,
  })), [jobsQ.data, user]);

  // Projekte für den Foto-Upload: /projekte-fotos liefert ALLE je zugeteilten
  // Projekte — deckungsgleich mit dem, was das Upload-Backend erlaubt. Die
  // frühere Liste (Plan-Einsätze + nur AKTIVE Projekte) ließ genau die fertigen
  // Baustellen weg, für die man Doku-Fotos nachreicht. Solange die Abfrage noch
  // lädt, dienen die Plan-Einsätze als Zwischenstand.
  const fotoProjekte: ProjektOption[] = useMemo(() => {
    const vomServer = fotoProjekteQ.data || [];
    if (vomServer.length > 0) return vomServer;
    const seen = new Map<string, string>();
    for (const e of einsaetze) if (e.projektId && !seen.has(e.projektId)) seen.set(e.projektId, e.projekt);
    return [...seen].map(([id, title]) => ({ id, title }));
  }, [einsaetze, fotoProjekteQ.data]);

  const auszahlungen: Auszahlung[] = useMemo(() =>
    (saldoQ.data?.auszahlungen || []).map(a => ({
      id: a.id, betrag: a.betrag, datum: a.zahlungsdatum, vermerk: a.vermerk || "", belegId: a.beleg_id || "",
    })), [saldoQ.data]);

  const rechnungen: RechnungV[] = useMemo(() => (rechnungenQ.data || []) as RechnungV[], [rechnungenQ.data]);

  const summary = useMemo(() => {
    const sal = saldoQ.data;
    const now = new Date(); const cutoff = new Date(now); cutoff.setDate(now.getDate() - 31);
    const monatStd = hours.filter(h => h.startAt && new Date(h.startAt) >= cutoff).reduce((s, h) => s + h.erfasstStd, 0);
    const toConfirm = hours.filter(h => h.status === "zu_bestaetigen").length;
    const einladungenOffen = einsaetze.filter(e => e.einsatzStatus === "wartet_auf_helfer").length;
    return {
      offenBetrag: sal?.offen ?? 0, offenStd: sal?.stunden_bestaetigt ?? 0,
      verdientGesamt: sal?.verdient ?? 0,
      ausgezahltGesamt: sal?.ausgezahlt ?? 0, monatStd,
      einsaetzeGesamt: hours.length, toConfirm, einladungenOffen,
      naechster: einsaetze[0] || null, weitere: einsaetze.slice(1, 3),
      naechsteAuszahlung: null as string | null,
      pauschalen: sal?.pauschalen ?? [],
      pauschaleSumme: sal?.pauschale_summe ?? 0,
    };
  }, [saldoQ.data, hours, einsaetze]);

  // Aktionen
  const confirmHour = (id: string) => bestaetigen.mutate(id);
  /** Der Helfer trägt seine Zeit ein: Beginn, Ende, Pause. Seit 20.08.2026 der
   *  einzige Weg, auf dem Ist-Zeit entsteht — die Stempeluhr ist abgeschaltet. */
  const submitZeit = (slotId: string, datum: string, von: string, bis: string, pause: number, notiz: string) => {
    // Naiv-lokaler ISO wie der Planer ("YYYY-MM-DDTHH:MM:00"), damit es 1:1 dort ankommt.
    const start_at = `${datum}T${von}:00`;
    const end_at = `${datum}T${bis}:00`;
    return anpassen.mutateAsync({ slotId, body: { start_at, end_at, pause_stunden: pause, kommentar: notiz } });
  };
  const submitAuslagen = (hour: Hour, betrag: number, note: string) =>
    auslagen.mutateAsync({ slotId: hour.id, body: { zusatzkosten: betrag, note } });
  const submitBewerbung = (terminId: string) => bewerben.mutateAsync(terminId);
  const zusageEinsatz = (slotId: string) => einsatzZusagen.mutateAsync(slotId);
  const absageEinsatz = (slotId: string, grund = "") => einsatzAbsagen.mutateAsync({ slotId, grund });
  const submitNeu = (body: { projekt_id: string; datum: string; start: string; ende: string; pause_stunden: number; kommentar?: string; verguetungsart?: string; pauschale_id?: string }) => neueStunden.mutateAsync(body);
  const submitRechnung = (args: RechnungUploadArgs) => rechnungUpload.mutateAsync(args);
  const submitErsetzen = (belegId: string, datei: File) => rechnungErsetzen.mutateAsync({ belegId, datei });
  const submitFotos = (args: FotoUploadArgs) => fotoUpload.mutateAsync(args);
  const submitLieferschein = (args: LieferscheinArgs) => lieferschein.mutateAsync(args);

  const loading = me.isLoading || stundenQ.isLoading || saldoQ.isLoading;
  const error = me.isError;

  return {
    user, hours, einsaetze, abgelehnteBewerbungen, jobs, auszahlungen, rechnungen, summary,
    // Nachrichten der Bauleitung (verschoben/abgesagt) — der einzige Weg, auf
    // dem den Helfer eine Aenderung ueberhaupt erreicht.
    nachrichten: (nachrichtenQ.data || []) as Benachrichtigung[],
    nachrichtenUngelesen: (nachrichtenQ.data || []).filter(n => !n.gelesen).length,
    nachrichtenAlsGelesen: () => nachrichtenGelesen.mutate(),
    projekte: (projekteQ.data || []) as ProjektOption[],
    fotoProjekte,
    // Material geht an jede laufende Baustelle, nicht nur an die eigenen.
    aktiveProjekte: (aktiveProjekteQ.data || []) as ProjektOption[],
    loading, error,
    confirmHour, submitZeit, submitAuslagen, submitBewerbung, zusageEinsatz, absageEinsatz,
    submitNeu, submitRechnung, submitErsetzen, submitFotos, submitLieferschein,
    rechnungDateiUrl,
    // Nur lesend: welche Pauschalen die Bauleitung vereinbart hat. Vorschlagen
    // kann der Helfer sie nicht (Entscheidung 10.08.2026).
    projektpreise: (projektpreiseQ.data || []),
    rechnungBusy: rechnungUpload.isPending,
    ersetzenBusy: rechnungErsetzen.isPending,
    fotoBusy: fotoUpload.isPending,
    lieferscheinBusy: lieferschein.isPending,
    busy: bestaetigen.isPending || anpassen.isPending || auslagen.isPending || bewerben.isPending
      || einsatzZusagen.isPending || einsatzAbsagen.isPending || neueStunden.isPending,
    // Sucht in BEIDEN Listen — auch eine abgelehnte Bewerbung laesst sich oeffnen.
    byEinsatz: (id: string) => alleEinsaetze.find(e => e.id === id) || null,
  };
}

export type TeamModel = ReturnType<typeof useTeamModel>;
